import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, HeartPulse, Move, Leaf, Zap, CalendarCheck } from "lucide-react";

const BENEFITS = [
  { icon: ShieldCheck, label: "Cartilage Protection" },
  { icon: HeartPulse, label: "Joint Comfort" },
  { icon: Move, label: "Supports Mobility" },
  { icon: Leaf, label: "Long-term Joint Health" },
];

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.25 + i * 0.14, duration: 1, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const boxY = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden bg-gradient-to-br from-white via-fv-ice to-fv-mist pt-[72px]"
    >
      <div className="pointer-events-none absolute -left-40 top-20 h-[480px] w-[480px] rounded-full bg-fv-blue/10 blur-3xl" />

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-40 pt-16 lg:grid-cols-2 lg:gap-8 lg:pb-48 lg:pt-24 lg:px-8">
        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mb-7 inline-flex items-center gap-2 rounded-full border border-fv-blue/25 bg-white/80 px-4 py-1.5 text-[11px] font-bold tracking-[0.2em] text-fv-blue backdrop-blur"
          >
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-fv-blue" />
            4-IN-1 JOINT SUPPORT SUPPLEMENT
          </motion.div>

          <h1 className="headline-xl text-[19vw] text-fv-navy sm:text-7xl lg:text-[86px] xl:text-[100px]" data-testid="hero-headline">
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                variants={lineReveal}
                initial="hidden"
                animate="show"
                custom={0}
              >
                Mobility
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span
                className="block text-fv-blue"
                variants={lineReveal}
                initial="hidden"
                animate="show"
                custom={1}
              >
                that lasts.
              </motion.span>
            </span>
          </h1>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-md text-base leading-relaxed text-fv-navy/65 md:text-lg"
          >
            Advanced 4-in-1 formula to support joint comfort, cartilage
            protection and long-term mobility.
          </motion.p>

          <motion.div
            data-testid="hero-benefits"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12 grid max-w-lg grid-cols-2 gap-x-6 gap-y-6 sm:grid-cols-4 sm:gap-3"
          >
            {BENEFITS.map(({ icon: Icon, label }, i) => (
              <div key={i} className="group flex flex-col items-start gap-3" data-testid={`hero-benefit-${i}`}>
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-fv-blue/25 bg-white shadow-sm transition-all duration-300 group-hover:-translate-y-1 group-hover:border-fv-blue/50 group-hover:shadow-fv-blue/20 group-hover:shadow-lg">
                  <Icon className="h-5 w-5 text-fv-blue" strokeWidth={1.6} />
                </div>
                <span className="text-xs font-bold leading-snug tracking-wide text-fv-navy/80">
                  {label}
                </span>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="relative mx-auto flex w-full max-w-[560px] items-center justify-center" data-testid="hero-visual">
          <motion.div
            style={{ y: boxY }}
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative flex items-center justify-center py-10"
          >
            <div className="fv-spotlight absolute -inset-24 -z-10" />
            <div className="absolute h-[460px] w-[460px] rounded-full border border-dashed border-fv-blue/30 sm:h-[560px] sm:w-[560px]" aria-hidden="true" />
            <div className="absolute h-[340px] w-[340px] rounded-full border border-fv-blue/15 sm:h-[420px] sm:w-[420px]" aria-hidden="true" />
            <motion.img
              src="/assets/fellesviar-box.png"
              alt="FellesViar Capsule product box — 3 x 10 vegetable capsules"
              data-testid="product-box"
              animate={{ y: [0, -16, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
              className="relative z-10 w-[320px] drop-shadow-[0_50px_50px_rgba(10,25,47,0.35)] sm:w-[440px] lg:w-[500px]"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.1, duration: 0.7 }}
              className="absolute -left-2 top-16 z-20 flex items-center gap-2 rounded-2xl border border-fv-blue/20 bg-white/90 px-4 py-2.5 shadow-xl shadow-fv-blue/10 backdrop-blur sm:-left-8"
              data-testid="hero-chip-relief"
            >
              <Zap className="h-4 w-4 fill-fv-blue text-fv-blue" />
              <span className="text-xs font-bold text-fv-navy">Relief in 5–7 days</span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.3, duration: 0.7 }}
              className="absolute -right-2 bottom-20 z-20 flex items-center gap-2 rounded-2xl border border-fv-blue/20 bg-white/90 px-4 py-2.5 shadow-xl shadow-fv-blue/10 backdrop-blur sm:-right-6"
              data-testid="hero-chip-daily"
            >
              <CalendarCheck className="h-4 w-4 text-fv-blue" />
              <span className="text-xs font-bold text-fv-navy">1 capsule daily · 30's</span>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <svg
        viewBox="0 0 1440 140"
        preserveAspectRatio="none"
        className="absolute bottom-0 left-0 h-[90px] w-full sm:h-[130px]"
        aria-hidden="true"
      >
        <path
          d="M0,80 C360,140 1080,10 1440,70 L1440,140 L0,140 Z"
          fill="#0A192F"
        />
      </svg>
    </section>
  );
}
